/**
 * Standardized Error Display Component
 *
 * Provides consistent error UI across the application
 * with different variants for different contexts.
 */

import {
  type AppErrorClass,
  ErrorType,
  createAppError,
} from "@/utils/errorHandler"
import {
  Box,
  Button,
  CloseButton,
  Heading,
  Text,
  VStack,
} from "@chakra-ui/react"
import React from "react"

interface ErrorDisplayProps {
  error: unknown
  title?: string
  variant?: "alert" | "page" | "inline" | "toast"
  onRetry?: () => void
  onDismiss?: () => void
  showDetails?: boolean
}

/**
 * Get error colors based on error type
 */
function getErrorColors(error: AppErrorClass): {
  bg: string
  border: string
  text: string
} {
  switch (error.code) {
    case ErrorType.AUTHENTICATION:
    case ErrorType.AUTHORIZATION:
      return { bg: "orange.50", border: "orange.200", text: "orange.700" }
    case ErrorType.VALIDATION:
      return { bg: "blue.50", border: "blue.200", text: "blue.700" }
    default:
      return { bg: "red.50", border: "red.200", text: "red.700" }
  }
}

/**
 * Alert variant - for inline error messages
 */
const AlertVariant: React.FC<{
  error: AppErrorClass
  title?: string
  onRetry?: () => void
  onDismiss?: () => void
  showDetails?: boolean
}> = ({ error, title, onRetry, onDismiss, showDetails }) => {
  const colors = getErrorColors(error)

  return (
    <Box
      bg={colors.bg}
      borderColor={colors.border}
      borderWidth="1px"
      borderRadius="md"
      p={4}
    >
      <Box display="flex" alignItems="center" w="full">
        <Box flex="1">
          {title && (
            <Text fontWeight="bold" color={colors.text} mb={1}>
              {title}
            </Text>
          )}
          <Text color={colors.text}>{error.message}</Text>
        </Box>
        {onDismiss && <CloseButton onClick={onDismiss} />}
      </Box>

      {(onRetry || showDetails) && (
        <Box mt={4} w="full">
          <VStack gap={2} align="flex-start">
            {onRetry && (
              <Button size="sm" colorScheme="blue" onClick={onRetry}>
                Try Again
              </Button>
            )}

            {showDetails && import.meta.env.DEV && error.details && (
              <Box
                as="details"
                bg="gray.50"
                p={3}
                borderRadius="md"
                fontSize="sm"
                w="full"
              >
                <Text as="summary" cursor="pointer" fontWeight="bold">
                  Error Details
                </Text>
                <Text fontFamily="mono" mt={2} fontSize="xs">
                  Code: {error.code}
                  {error.status && ` | Status: ${error.status}`}
                  {error.details && (
                    <Box as="pre" mt={1} whiteSpace="pre-wrap">
                      {JSON.stringify(error.details, null, 2)}
                    </Box>
                  )}
                </Text>
              </Box>
            )}
          </VStack>
        </Box>
      )}
    </Box>
  )
}

/**
 * Page variant - for full page error states
 */
const PageVariant: React.FC<{
  error: AppErrorClass
  title?: string
  onRetry?: () => void
  showDetails?: boolean
}> = ({ error, title, onRetry, showDetails }) => {
  return (
    <Box p={8} maxW="lg" mx="auto" textAlign="center">
      <VStack gap={6}>
        <Box>
          <Heading size="lg" mb={2}>
            {title || "Something went wrong"}
          </Heading>
          <Text color="gray.600" fontSize="lg">
            {error.message}
          </Text>
        </Box>

        <VStack gap={4}>
          {onRetry && (
            <Button colorScheme="blue" size="lg" onClick={onRetry}>
              Try Again
            </Button>
          )}

          <Button variant="ghost" onClick={() => window.location.reload()}>
            Reload Page
          </Button>
        </VStack>

        {showDetails && import.meta.env.DEV && (
          <Box
            bg="gray.50"
            p={4}
            borderRadius="md"
            fontSize="sm"
            textAlign="left"
            w="full"
          >
            <Text fontWeight="bold" mb={2}>
              Error Details (Development)
            </Text>
            <Text fontFamily="mono" fontSize="xs">
              Code: {error.code}
              {error.status && ` | Status: ${error.status}`}
            </Text>
            {error.stack && (
              <Box as="pre" mt={2} whiteSpace="pre-wrap" fontSize="xs">
                {error.stack}
              </Box>
            )}
          </Box>
        )}
      </VStack>
    </Box>
  )
}

/**
 * Inline variant - for small error messages
 */
const InlineVariant: React.FC<{
  error: AppErrorClass
  onRetry?: () => void
  onDismiss?: () => void
}> = ({ error, onRetry, onDismiss }) => {
  return (
    <Box
      p={3}
      bg="red.50"
      borderColor="red.200"
      borderWidth={1}
      borderRadius="md"
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Text color="red.700" fontSize="sm">
          {error.message}
        </Text>
        <Box>
          {onRetry && (
            <Button
              size="xs"
              colorScheme="red"
              variant="ghost"
              onClick={onRetry}
            >
              Retry
            </Button>
          )}
          {onDismiss && <CloseButton size="sm" onClick={onDismiss} ml={2} />}
        </Box>
      </Box>
    </Box>
  )
}

/**
 * Main ErrorDisplay component with variant switching
 */
export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  title,
  variant = "alert",
  onRetry,
  onDismiss,
  showDetails = false,
}) => {
  // Convert to AppError for consistent handling
  const appError = React.useMemo(() => createAppError(error), [error])

  switch (variant) {
    case "page":
      return (
        <PageVariant
          error={appError}
          title={title}
          onRetry={onRetry}
          showDetails={showDetails}
        />
      )

    case "inline":
      return (
        <InlineVariant
          error={appError}
          onRetry={onRetry}
          onDismiss={onDismiss}
        />
      )
    default:
      return (
        <AlertVariant
          error={appError}
          title={title}
          onRetry={onRetry}
          onDismiss={onDismiss}
          showDetails={showDetails}
        />
      )
  }
}

export default ErrorDisplay
